import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import cx from "ui/cx";
import IconSearch from "ui/icons/IconSearch";
import styles from "./SearchPanel.module.scss";

export interface SearchPanelItem {
  readonly id: string;
  readonly label: string;
  readonly typeLabel: string;
}

export interface SearchPanelProps<T extends SearchPanelItem> {
  readonly items: readonly T[];
  readonly onSelect: (item: T) => void;
  readonly closeModal: () => void;
  readonly filterItems?: (items: readonly T[], query: string) => readonly T[];
  /**
   * Resolves an extra class for an item's row, applied after the component's
   * default classes. Consumers use this to layer domain-specific hierarchy
   * (e.g. indent category vs. option rows) onto the generic result list.
   * The returned string should already be scoped — typically `styles.kind` from
   * the consumer's own CSS module — so the component stays domain-agnostic.
   */
  readonly getItemClassName?: (item: T) => string | undefined;
  readonly title?: string;
  readonly placeholder?: string;
  readonly noResultsMessage?: string | ((query: string) => string);
  readonly maxResults?: number;
}

function formatNoResults(query: string): string {
  return `No results for "${query}"`;
}

function defaultFilter<T extends { label: string }>(items: readonly T[], query: string): readonly T[] {
  const lowerQuery = query.toLocaleLowerCase().trim();
  if (!lowerQuery) {
    return items;
  }
  return items.filter((item) => item.label.toLocaleLowerCase().includes(lowerQuery));
}

export default function SearchPanel<T extends SearchPanelItem>({
  items,
  onSelect,
  closeModal,
  filterItems = defaultFilter,
  getItemClassName,
  title = "Search",
  placeholder = "Search…",
  noResultsMessage = formatNoResults,
  maxResults,
}: SearchPanelProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const titleId = useId();
  const [query, setQuery] = useState("");
  const [pendingQuery, setPendingQuery] = useState("");
  const [, startTransition] = useTransition();
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = filterItems(items, pendingQuery);
  const results = maxResults !== undefined ? filtered.slice(0, maxResults) : filtered;
  const activeHit = results[activeIndex];

  // Portal + trigger/⌘K can race mount-time focus; reclaim after layout and next task.
  useLayoutEffect(() => {
    const focusInput = () => {
      inputRef.current?.focus();
    };
    focusInput();
    const frameId = window.requestAnimationFrame(focusInput);
    const timeoutId = window.setTimeout(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        focusInput();
      }
    }, 0);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [pendingQuery]);

  useEffect(() => {
    if (activeIndex >= results.length) {
      setActiveIndex(results.length > 0 ? results.length - 1 : 0);
    }
  }, [results.length, activeIndex]);

  const onListMouseOver = (event: MouseEvent<HTMLUListElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const row = target.closest("[data-row-index]");
    if (!row) {
      return;
    }
    const index = Number(row.getAttribute("data-row-index"));
    if (Number.isInteger(index) && index >= 0 && index < results.length && index !== activeIndex) {
      setActiveIndex(index);
    }
  };

  const selectActive = () => {
    if (activeHit) {
      onSelect(activeHit);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      setActiveIndex((prev) => (prev + 1) % results.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectActive();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
    }
  };

  const onQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQuery(next);
    startTransition(() => {
      setPendingQuery(next);
    });
  };

  const trimmed = query.trim();
  const showNoResults = trimmed.length > 0 && results.length === 0;
  const resolvedNoResults = typeof noResultsMessage === "function" ? noResultsMessage(trimmed) : noResultsMessage;

  return (
    <div className={styles.root} onKeyDown={onKeyDown}>
      <h2 id={titleId} className={styles.visuallyHidden}>
        {title}
      </h2>
      <div className={styles.inputRow}>
        <IconSearch className={styles.searchIcon} aria-hidden />
        {/* Not SearchInput: its Escape clears/blurs and chrome conflicts with command-palette close. */}
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          className={styles.input}
          placeholder={placeholder}
          value={query}
          onChange={onQueryChange}
          aria-labelledby={titleId}
          aria-controls={listId}
          aria-expanded
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={activeHit ? `${listId}-option-${activeIndex}` : undefined}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
      </div>
      {showNoResults && <div className={styles.message}>{resolvedNoResults}</div>}
      <ul id={listId} role="listbox" className={styles.list} aria-label="Search results" onMouseOver={onListMouseOver}>
        {results.map((hit, index) => {
          const selected = index === activeIndex;
          return (
            <li
              key={hit.id}
              id={`${listId}-option-${index}`}
              data-row-index={index}
              role="option"
              aria-selected={selected}
              className={cx(styles.option, selected && styles.optionActive, getItemClassName && getItemClassName(hit))}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => onSelect(hit)}
            >
              <span className={styles.optionLabel}>{hit.label}</span>
              <span className={styles.optionType}>{hit.typeLabel}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
